import { motion } from "framer-motion";
import { Send } from "lucide-react";
import * as React from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";

export default function RegistrationSection() {
  const [formData, setFormData] = React.useState({
    fullName: "",
    email: "",
    whatsapp: "",
    capital: "",
    experience: "",
    readyToStart: "",
    location: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.fullName || !formData.email || !formData.whatsapp) {
      toast.error("الرجاء تعبئة جميع الحقول المطلوبة");
      return;
    }

    const numbers = ["21628850388", "21628830388"];
    const lastUsedIndex = parseInt(
      localStorage.getItem("lastWhatsAppIndex") || "0",
      10
    );

    // currentIndex يحدد الرقم المستخدم هذه المرة (يتبدّل كل إرسال)
    const currentIndex = lastUsedIndex === 0 ? 1 : 0;
    const currentNumber = numbers[currentIndex];

    localStorage.setItem("lastWhatsAppIndex", currentIndex.toString());

    const message = `
مرحبًا، لقد تم تسجيل بياناتي في التحدي المجاني:

* الاسم الكامل: ${formData.fullName}
* البريد الإلكتروني: ${formData.email}
* رقم الواتساب: ${formData.whatsapp}
* رأس المال المتاح: ${formData.capital || "غير محدد"}
* خبرة التداول: ${formData.experience || "غير محددة"}
* جاهز تبدأ خلال 24 ساعة؟: ${formData.readyToStart || "غير محدد"}
* الدولة / المدينة: ${formData.location || "غير محددة"}
  `;

    const whatsappLink = `https://wa.me/${currentNumber}?text=${encodeURIComponent(
      message
    )}`;

    // فتح رابط الواتساب للرقم المحدد فقط (المتصفح يسمح لأنه داخل حدث المستخدم)
    window.open(whatsappLink, "_blank");

    // إرسال البيانات إلى Google Sheets
    try {
      await sendToGoogleSheets(formData);
      toast.success(
        `تم فتح واتساب مع بياناتك وحفظ البيانات! (الرقم المستخدم: ${currentNumber})`
      );
    } catch (error) {
      console.error("Error sending to Google Sheets:", error);
      toast.success("تم فتح واتساب مع بياناتك! سنتواصل معك قريبًا.");
    }

    // Reset form
    setFormData({
      fullName: "",
      email: "",
      whatsapp: "",
      capital: "",
      experience: "",
      readyToStart: "",
      location: "",
    });
  };

  const sendToGoogleSheets = async (data: typeof formData) => {
    const GOOGLE_SCRIPT_URL = import.meta.env.VITE_GOOGLE_SHEET_URL;

    if (!GOOGLE_SCRIPT_URL) {
      console.warn("Google Sheets URL not configured");
      return;
    }

    await fetch(GOOGLE_SCRIPT_URL, {
      method: "POST",
      mode: "no-cors",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...data,
        capital: data.capital || "غير محدد",
        experience: data.experience || "غير محددة",
        readyToStart: data.readyToStart || "غير محدد",
        location: data.location || "غير محددة",
        timestamp: new Date().toISOString(),
      }),
    });
  };

  return (
    <section
      id="register"
      className="relative py-24 bg-gradient-to-br from-[#0A1E3F] via-[#0A1E3F]/90 to-[#000000] overflow-hidden"
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#CFAE58] rounded-full blur-[200px] opacity-10"></div>

      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#CFAE58] to-transparent"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              سجّل الآن وابدأ{" "}
              <span className="text-[#CFAE58]">التحدي المجاني</span>
            </h2>
            <p className="text-xl text-gray-300">
              املأ البيانات وسنتواصل معك خلال 24 ساعة
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="absolute -inset-[2px] bg-gradient-to-br from-[#CFAE58]/30 to-[#CFAE58]/10 rounded-3xl blur-xl"></div>

            <div className="relative bg-gradient-to-br from-[#0A1E3F]/80 to-[#000000]/80 backdrop-blur-sm border-2 border-[#CFAE58]/30 rounded-3xl p-8 md:p-12">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="fullName" className="text-white mb-2 block">
                    الاسم الكامل *
                  </Label>
                  <Input
                    id="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={(e) =>
                      setFormData({ ...formData, fullName: e.target.value })
                    }
                    className="bg-white/10 border-[#CFAE58]/30 text-white placeholder:text-gray-400 focus:border-[#CFAE58] h-12"
                    placeholder="أدخل اسمك الكامل"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-white mb-2 block">
                    البريد الإلكتروني *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="bg-white/10 border-[#CFAE58]/30 text-white placeholder:text-gray-400 focus:border-[#CFAE58] h-12"
                    placeholder="example@email.com"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="whatsapp" className="text-white mb-2 block">
                    رقم الواتساب *
                  </Label>
                  <Input
                    id="whatsapp"
                    type="tel"
                    value={formData.whatsapp}
                    onChange={(e) =>
                      setFormData({ ...formData, whatsapp: e.target.value })
                    }
                    className="bg-white/10 border-[#CFAE58]/30 text-white placeholder:text-gray-400 focus:border-[#CFAE58] h-12"
                    placeholder="+966 XX XXX XXXX"
                    required
                    dir="ltr"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="capital" className="text-white mb-2 block">
                      رأس المال المتاح
                    </Label>
                    <Select
                      value={formData.capital}
                      onValueChange={(value) =>
                        setFormData({ ...formData, capital: value })
                      }
                    >
                      <SelectTrigger
                        className="bg-white/10 border-[#CFAE58]/30 text-white h-12"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SelectValue placeholder="اختر المبلغ" />
                      </SelectTrigger>
                      <SelectContent
                        position="popper"
                        sideOffset={4}
                        className="bg-white text-[#0A1E3F] border-[#CFAE58]/30 z-[9999]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SelectItem value="100-500">100$ - 500$</SelectItem>
                        <SelectItem value="500-1000">500$ - 1000$</SelectItem>
                        <SelectItem value="1000-5000">1000$ - 5000$</SelectItem>
                        <SelectItem value="5000+">أكثر من 5000$</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="experience" className="text-white mb-2 block">
                      خبرة التداول
                    </Label>
                    <Select
                      value={formData.experience}
                      onValueChange={(value) =>
                        setFormData({ ...formData, experience: value })
                      }
                    >
                      <SelectTrigger
                        className="bg-white/10 border-[#CFAE58]/30 text-white h-12"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SelectValue placeholder="اختر مستواك" />
                      </SelectTrigger>
                      <SelectContent
                        position="popper"
                        sideOffset={4}
                        className="bg-white text-[#0A1E3F] border-[#CFAE58]/30 z-[9999]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SelectItem value="beginner">مبتدئ</SelectItem>
                        <SelectItem value="intermediate">متوسط</SelectItem>
                        <SelectItem value="advanced">متقدم</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="readyToStart" className="text-white mb-2 block">
                    جاهز تبدأ خلال 24 ساعة؟
                  </Label>
                  <Select
                    value={formData.readyToStart}
                    onValueChange={(value) =>
                      setFormData({ ...formData, readyToStart: value })
                    }
                  >
                    <SelectTrigger
                      className="bg-white/10 border-[#CFAE58]/30 text-white h-12"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SelectValue placeholder="اختر" />
                    </SelectTrigger>
                    <SelectContent
                      position="popper"
                      sideOffset={4}
                      className="bg-white text-[#0A1E3F] border-[#CFAE58]/30 z-[9999]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SelectItem value="yes">نعم، جاهز</SelectItem>
                      <SelectItem value="no">لا، محتاج وقت</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="location" className="text-white mb-2 block">
                    الدولة / المدينة
                  </Label>
                  <Input
                    id="location"
                    type="text"
                    value={formData.location}
                    onChange={(e) =>
                      setFormData({ ...formData, location: e.target.value })
                    }
                    className="bg-white/10 border-[#CFAE58]/30 text-white placeholder:text-gray-400 focus:border-[#CFAE58] h-12"
                    placeholder="مثلاً: الرياض، السعودية"
                  />
                </div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-[#CFAE58] text-white py-5 rounded-xl hover:bg-[#B89A45] transition-all duration-300 shadow-[0_0_25px_rgba(207,174,88,0.8),_0_0_50px_rgba(207,174,88,0.5)] flex items-center justify-center gap-3 text-xl mt-8"
                >
                  <span className="drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]">
                    سجّل الآن وابدأ التحدي
                  </span>
                  <Send className="w-6 h-6 drop-shadow-[0_0_10px_rgba(255,255,255,0.8)]" />
                </motion.button>

                <p className="text-gray-400 text-center text-sm mt-4">
                  سنتواصل معك عبر واتساب خلال 24 ساعة لتأكيد التسجيل.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
